/**
 * PromptItem Component (SWRM-103)
 * 
 * A single prompt item in the prompts list showing:
 * - Prompt name and type badge
 * - Preview of content (truncated)
 * - Edit button
 * - Custom badge if prompt is customized
 */

import React from 'react';
import { Edit2, Check } from 'lucide-react';
import { PromptData } from '../../types/pipeline';

interface PromptItemProps {
  promptName: string;
  promptData: PromptData;
  currentContent: string;
  onEdit: () => void;
}

export const PromptItem: React.FC<PromptItemProps> = ({
  promptName,
  promptData,
  currentContent,
  onEdit,
}) => {
  // Check if prompt is customized (different from default)
  const isCustomized = currentContent !== promptData.content;

  // Truncate content for preview
  const preview = currentContent.length > 100
    ? currentContent.substring(0, 100) + '...'
    : currentContent;

  return (
    <div 
      className="border border-gray-200 dark:border-gray-700 rounded p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {promptName}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${
              promptData.type === 'system'
                ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
            }`}>
              {promptData.type}
            </span>
            {isCustomized && (
              <span
                className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 flex items-center gap-1"
                title="This prompt has been customized"
              >
                <Check className="w-3 h-3" />
                Custom
              </span>
            )}
          </div>

          {/* Description */}
          {promptData.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {promptData.description}
            </p>
          )}

          {/* Preview */}
          <p className="text-xs text-gray-600 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-900 p-2 rounded">
            {preview}
          </p>

          {/* Variables */}
          {promptData.variables.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {promptData.variables.slice(0, 3).map((variable) => (
                <code
                  key={variable}
                  className="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300"
                >
                  {`{{${variable}}}`}
                </code>
              ))}
              {promptData.variables.length > 3 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  +{promptData.variables.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Edit Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="flex-shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
          title="Edit prompt"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

